import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SEO from "../components/SEO";
import {
  Linkedin,
  Mail,
  MapPin,
  Phone,
  CheckCircle,
  Award,
  Briefcase,
  GraduationCap,
  Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AboutPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! I'll get back to you soon.");
      setFormData({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const experience = [
    {
      title: "Lead Compliance Engineer",
      company: "ResMed",
      location: "Bothell, WA",
      period: "2021 - Present",
      highlights: [
        "Led compliance strategy for global SaMD platforms",
        "Prepared FDA PCCP plans and Q-Sub components",
        "100% audit success rate across multiple products",
        "Reduced documentation cycle time by 30%",
      ],
    },
    {
      title: "Senior Quality Engineer",
      company: "Agiliti Health",
      period: "2020 - 2021",
      highlights: [
        "Led post-market surveillance for Class II devices",
        "Managed team of 7 quality engineers",
      ],
    },
    {
      title: "Quality Engineer",
      company: "Breas Medical",
      period: "2018 - 2020",
      highlights: [
        "Managed CAPA and nonconformance processes",
        "Implemented QMS workflows in JIRA",
      ],
    },
    {
      title: "Quality Engineer",
      company: "EchoNous",
      period: "2017 - 2018",
      highlights: [
        "Supported 510(k) and CE Marking for ultrasound device",
        "Authored dFMEA and pFMEA documents",
      ],
    },
  ];

  const certifications = [
    "ISO 13485:2016 Lead Auditor (BSI)",
    "NIST AI Risk Management Framework",
    "Python for Data Science (Coursera)",
    "ML Models - Google Cloud",
  ];

  const skills = [
    "FDA 510(k)", "CE Marking", "PCCP", "EU AI Act",
    "ISO 13485", "ISO 14971", "Python", "SQL", "JIRA"
  ];

  return (
    <>
      <SEO
        title="About Saloni Shah | AI Governance & MedTech Compliance Expert"
        description="Learn about Saloni Shah's expertise in regulatory AI, compliance strategy, and 8+ years helping companies ship SaMD & AI-enabled products."
        url="/about"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-neutral-50" data-testid="about-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-serif font-bold text-4xl md:text-5xl text-neutral-900 mb-4">
            About Me
          </h1>
          <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
            Compliance & Risk Leader with 8+ years helping MedTech companies navigate regulatory complexity.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 bg-white" data-testid="story-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-neutral-600 leading-relaxed mb-6">
              With a Master's degree in Bioengineering from SUNY Binghamton and extensive global regulatory experience, 
              I've dedicated my career to bridging the gap between AI innovation and regulatory compliance in healthcare.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-6">
              Currently serving as Lead Compliance Engineer at ResMed, I specialize in Software as a Medical Device (SaMD) 
              platforms and AI/ML compliance frameworks. My expertise spans FDA 510(k) submissions, CE marking, and 
              Predetermined Change Control Plans (PCCP) for AI-enabled medical devices.
            </p>
            <p className="text-neutral-600 leading-relaxed">
              I believe that clear communication about risk and compliance is essential for successful AI deployment 
              in regulated industries. My goal is to help companies safely bring innovations to market while maintaining 
              the highest standards of patient safety.
            </p>
          </div>

          {/* Key Achievements */}
          <div className="mt-12 p-6 bg-primary/5 rounded-xl">
            <h3 className="font-serif font-semibold text-xl text-neutral-900 mb-4">Key Achievements</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                "100% audit success rate",
                "12+ successful submissions",
                "30% cycle time reduction",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span className="text-neutral-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section className="py-16 bg-neutral-50" data-testid="experience-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-serif font-semibold text-3xl text-neutral-900 mb-8 flex items-center gap-3">
            <Briefcase className="w-8 h-8 text-primary" />
            Experience
          </h2>

          <div className="space-y-6">
            {experience.map((job, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl border border-neutral-100"
                data-testid={`experience-${index}`}
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2 mb-3">
                  <div>
                    <h3 className="font-serif font-semibold text-lg text-neutral-900">{job.title}</h3>
                    <p className="text-primary font-medium">{job.company}</p>
                  </div>
                  <span className="text-sm text-neutral-500">{job.period}</span>
                </div>
                <ul className="text-sm text-neutral-600 space-y-1">
                  {job.highlights.map((h, i) => (
                    <li key={i}>• {h}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education & Skills */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Education */}
            <div className="bg-neutral-50 p-6 rounded-xl">
              <h3 className="font-serif font-semibold text-xl text-neutral-900 mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                Education
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="font-medium text-neutral-900">MS Bioengineering</p>
                  <p className="text-sm text-neutral-600">SUNY Binghamton • 2017</p>
                </div>
                <div>
                  <p className="font-medium text-neutral-900">BS Biomedical Engineering</p>
                  <p className="text-sm text-neutral-600">University of Mumbai • 2014</p>
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div className="bg-neutral-50 p-6 rounded-xl">
              <h3 className="font-serif font-semibold text-xl text-neutral-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-primary" />
                Certifications
              </h3>
              <ul className="space-y-2">
                {certifications.map((cert, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-neutral-700">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    {cert}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Skills */}
          <div className="mt-8">
            <h3 className="font-serif font-semibold text-xl text-neutral-900 mb-4">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill, i) => (
                <span key={i} className="bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 bg-neutral-50" data-testid="contact-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h2 className="font-serif font-semibold text-3xl text-neutral-900 mb-8 text-center">
            Get In Touch
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Form */}
            <div className="lg:col-span-3 bg-white rounded-xl p-6 shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    data-testid="contact-company"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    data-testid="contact-subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Message *</label>
                  <textarea
                    rows={4}
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-2.5 border border-neutral-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-primary text-white hover:bg-primary-light rounded-lg py-3 font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {sending ? "Sending..." : <><Send className="w-4 h-4" /> Send Message</>}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-neutral-900 mb-4">Contact Info</h3>
                <div className="space-y-3 text-sm">
                  <a href="mailto:shahsaloni2912@gmail.com" className="flex items-center gap-3 text-neutral-600 hover:text-primary">
                    <Mail className="w-4 h-4" />
                    shahsaloni2912@gmail.com
                  </a>
                  <div className="flex items-center gap-3 text-neutral-600">
                    <Phone className="w-4 h-4" />
                    +1 (607) 761-9351
                  </div>
                  <div className="flex items-center gap-3 text-neutral-600">
                    <MapPin className="w-4 h-4" />
                    Bothell, WA
                  </div>
                </div>
              </div>

              <a
                href="https://www.linkedin.com/in/saloni-s-246823188"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                data-testid="linkedin-connect"
              >
                <Linkedin className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-medium text-neutral-900">LinkedIn</p>
                  <p className="text-sm text-neutral-600">Connect with me</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
